import { Component, inject } from '@angular/core';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { CurrentService } from 'src/app/services/current.service';
import { FrameType } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame-type';
import { Frame } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame';
import { FrameFinish } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame-finish';
import { Door } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door';
import { FinishesSubStep } from '../../models/finishes-sub-step.enum';
import { FinishesSubStepService } from '../../services/finishes-sub-step.service';

@Component({
  selector: 'ng-casto-closet-doors-standard-finishes-frames',
  templateUrl: './frames.component.html',
  styleUrls: ['./frames.component.scss'],
})
export class ClosetDoorsStandardFinishesFramesComponent {
  readonly SubStep = FinishesSubStep;

  /** Indique si les finitions du profilé sélectionné sont en cours de chargement. */
  loadingProfiles = false;
  /** Éléments fantômes affichés pendant le chargement (même nombre que les finitions C3). */
  readonly skeletonItems = [1, 2, 3];

  private readonly closetDoorsService = inject(ClosetDoorsStandardService);
  private readonly current = inject(CurrentService);
  private readonly subStepService = inject(FinishesSubStepService);

  readonly baseImgURL = this.closetDoorsService.apiUrl;

  get subStep(): FinishesSubStep { return this.subStepService.subStep(); }
  get frameTypes(): FrameType[] { return this.closetDoorsService.frameTypes; }
  get selectedFrameType(): FrameType | null { return this.closetDoorsService.selectedFrameType; }
  get frameProfiles(): Frame[] { return this.closetDoorsService.frameProfiles; }
  get selectedFrameProfile(): Frame | null { return this.closetDoorsService.selectedFrameProfile; }
  get profiles(): FrameFinish[] { return this.closetDoorsService.profiles; }
  get selectedProfile(): FrameFinish | null { return this.current.closetDoors.frameFinish; }

  toggleCollapse(): void {
    this.subStepService.toggle(FinishesSubStep.Finishes);
  }

  /**
   * Sélectionne un type de matière (Acier / Aluminium).
   * Recharge les profilés disponibles, auto-sélectionne le premier, puis recharge les finitions.
   * @param frameType type de matière sélectionné
   */
  selectFrameType(frameType: FrameType): void {
    this.closetDoorsService.selectedFrameType = frameType;
    const configId = this.current.closetDoors.id;
    const rangeId = this.current.closetDoors.range?.id;
    const doorTypeIds = this.current.closetDoors.doors
      .map((d: Door) => d.structure?.id)
      .filter((id): id is number => id != null);
    this.closetDoorsService
      .getFrames(frameType.id, configId, doorTypeIds, rangeId)
      .then(frameProfiles => {
        this.closetDoorsService.frameProfiles = frameProfiles;
        const defaultProfile = frameProfiles[0] ?? null;
        this.closetDoorsService.selectedFrameProfile = defaultProfile;
        this.loadingProfiles = true;
        return this.closetDoorsService.getStructures(
          this.current.closetDoors.id,
          this.current.closetDoors.range?.id,
          defaultProfile?.id
        );
      })
      .then(res => this.closetDoorsService.applyStructuresResult(res))
      .finally(() => { this.loadingProfiles = false; });
  }

  /**
   * Sélectionne un profilé (C3, C4, ALU…).
   * Recharge les finitions filtrées par ce profilé.
   * @param frameProfile profilé sélectionné
   */
  selectFrameProfile(frameProfile: Frame): void {
    this.closetDoorsService.selectedFrameProfile = frameProfile;
    this.loadingProfiles = true;
    this.closetDoorsService
      .getStructures(
        this.current.closetDoors.id,
        this.current.closetDoors.range?.id,
        frameProfile.id
      )
      .then(res => this.closetDoorsService.applyStructuresResult(res))
      .finally(() => { this.loadingProfiles = false; });
  }

  /**
   * Sélectionne une finition (couleur de profilé) commune à tous les vantaux.
   * @param frameFinish finition sélectionnée
   */
  selectProfile(frameFinish: FrameFinish): void {
    this.current.closetDoors.frameFinish = frameFinish;
    this.closetDoorsService.updateLayers();
    this.scrollDown();
  }

  private scrollDown(): void {
    if (document.documentElement.clientWidth < 767) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }
}
